/* eslint-disable @typescript-eslint/no-explicit-any */

import { AxiosError } from 'axios';
import { api, unauthedApi } from './core';
import { extractApiError } from './errors';

type ApiMethod = 'post' | 'put' | 'delete' | 'get';

interface ExecuteProps {
  auth?: boolean;

  method?: ApiMethod;

  path: string;

  payload?: Record<string, any>;

  responseType?: 'blob' | 'json' | 'arraybuffer';

  withCookies?: boolean;
}

export type apiCall<Result = any> = (params: ExecuteProps) => Promise<Result>;

const methodToParser: {
  [key in ApiMethod]?: (payload: ExecuteProps['payload']) => ExecuteProps['payload'];
} = {
  delete: (payload) => ({ data: payload }),

  get: (payload) => ({ params: payload }),
};

function parsePayload({
  method = 'post',
  payload = {},
}: Pick<ExecuteProps, 'method' | 'payload'>): Record<string, any> {
  const parser = methodToParser[method];

  return parser?.(payload) || payload;
}

export async function apiCall<Result>({
  path,
  payload = {},
  method = 'post',
  responseType,
  auth = true,
  withCookies = false,
}: ExecuteProps): Promise<Result> {
  const apiPayload = parsePayload({ method, payload });

  const instance = auth ? api : unauthedApi;

  const hasOnlyTwoParams = method === 'get';

  const configParams = hasOnlyTwoParams
    ? [{ ...apiPayload, responseType, withCredentials: withCookies }]
    : [apiPayload, { responseType, withCredentials: withCookies }];

  try {
    const { data } = await instance[method]<Result>(path, ...configParams);

    return data;
  } catch (err) {
    throw extractApiError(err as AxiosError);
  }
}

export interface SuccessData {
  ok: true;
}

export type PaginatedResult<T> = {
  items: T[];
  paginationToken?: string;
};

export type PaginationParams = {
  paginationToken?: string;
};
